import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, queryString } from "../../api/client";
import type { Contact, ListResponse } from "../../api/types";

// The route is /api/v1/contacts and the table is customers.contacts. There used to be an
// account record with people filed underneath it, which meant two rows described one customer
// and the second one went unmaintained. A customer is a person now, so the person is the
// address. The Go package doc in internal/customers records the same decision.

// venueId is the venue in the top bar, and the list follows it the way bookings and events do:
// a customer belongs to the venues they hold bookings at, and one with no bookings anywhere is
// shown under every venue. Pass "" for every venue the membership can see, which is what the
// customer picker inside a booking form wants.
export function useContacts(search: string, status: string, venueId: string) {
  return useQuery({
    queryKey: ["contacts", "list", search, status, venueId],
    queryFn: () =>
      api<ListResponse<Contact>>(
        `/api/v1/contacts${queryString({ q: search, status, venueId, limit: 100, offset: 0 })}`,
      ),
    // Searching changes the query key, so without this the list would empty itself between
    // keystrokes and flash a loading state at somebody who is still typing.
    placeholderData: keepPreviousData,
  });
}

export function useContact(contactId: string | undefined) {
  return useQuery({
    queryKey: ["contacts", "detail", contactId],
    queryFn: () => api<Contact>(`/api/v1/contacts/${contactId}`),
    enabled: Boolean(contactId),
  });
}

// Every write sends the whole record plus the version it was read at, so a targeted change such
// as archiving cannot silently blank the fields it did not touch and cannot overwrite an edit
// made since the record was loaded.
//
// Consent is included for a sharper reason than completeness. The server reads an absent
// marketingEmail as a withdrawal, so archiving somebody with a bare payload would revoke a
// consent nobody chose to revoke. Resending the recorded source unchanged also leaves
// recorded_at where it is, which is what keeps the date that proves when they agreed honest.
export function contactInput(contact: Contact): Record<string, unknown> {
  const granted = contact.marketingEmail.state === "granted";
  return {
    type: contact.type,
    status: contact.status,
    displayName: contact.displayName,
    organisation: contact.organisation,
    legalName: contact.legalName,
    email: contact.email,
    phone: contact.phone,
    roleTitle: contact.roleTitle,
    billingAddress: contact.billingAddress,
    taxReference: contact.taxReference,
    notes: contact.notes,
    tags: contact.tags,
    portalEnabled: contact.portalEnabled,
    marketingEmail: granted,
    marketingSource: granted ? contact.marketingEmail.source : "",
    version: contact.version,
  };
}

// Type decides whether the company fields mean anything: the server clears organisation and
// legal name for an individual, because somebody renting a hall for their own birthday acts
// for nobody but themselves.
export const contactTypeLabels: Record<string, string> = {
  individual: "Individual",
  organisation: "Organization",
  government: "Government",
  nonprofit: "Nonprofit",
};

// The states come from customers.preferences, which the customer portal writes to as well.
// "Withdrawn" has to read differently from no record at all: one is a person who said no, the
// other is a person nobody asked, and a send list that conflates them eventually mails somebody
// who explicitly opted out.
export const marketingStateLabels: Record<string, string> = {
  granted: "Consented",
  withdrawn: "Withdrawn",
  denied: "Declined",
  not_required: "Not required",
};
